import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '../ui/sidebar';
import { Alert, AlertDescription } from '../ui/alert';
import { ScrollArea } from '../ui/scroll-area';
import { useAuthProviderV2, useRoutesProvider } from '../../providers/provider-hooks';
import { isPathAllowedForRole } from '../../domains/auth/utils/is-path-allowed';
import { useSchoolUpdateCheck } from '../../layouts/hooks/useSchoolUpdateCheck';
import { NavUser } from './NavUser';
import { GROUP_LABELS, groupLinksByMenuGroup, grupuriDeAfisat } from './menu-groups';

const PROFESOR_PREVIEW_ONLY_PATHS = [
  '/exercitii-pentru-acasa',
  '/provocari',
];

export function AppSidebar() {
  const { userInfo } = useAuthProviderV2();
  const { PROFILE_LINKS, ROLES, NAV_LINKS_V2, PATHS, PAGES } = useRoutesProvider();
  const location = useLocation();
  const navigate = useNavigate();
  // Drawer-ul mobil (Sheet) ramanea deschis dupa navigare - `onClick` facea doar
  // `navigate`, fara sa inchida `Sheet`-ul. Pe desktop `isMobile` e `false`, deci
  // `setOpenMobile` nu are efect vizibil acolo (sidebar-ul fix nu foloseste `openMobile`).
  const { setOpenMobile } = useSidebar();
  const { shouldHideMenuItems } = useSchoolUpdateCheck(userInfo, location.pathname);

  return (
    <Sidebar
      collapsible="offcanvas"
      className="ecotic-sidebar"
    >
      <SidebarHeader className="ecotic-sidebar-section">
        <Link to="/" className="flex items-center justify-center p-2">
          <img src="/logo-ecotic.png" alt="ECOTIC logo" className="h-full max-h-[65px]" />
        </Link>
      </SidebarHeader>

      <SidebarContent className="ecotic-sidebar-section">
        <SidebarGroup className="flex min-h-0 flex-1 flex-col">
          {userInfo?.role === ROLES.PROFESOR && !(userInfo as any)?.schoolId && (
            <Alert className="mx-2 mb-4 border-2 border-orange-500 bg-orange-100">
              <AlertDescription className="text-center text-sm font-semibold text-orange-800">
                {/* A11, sedinta 18.09: textul vechi suna ca un verdict definitiv pentru un
                    profesor care tocmai si-a depus cererea. Starea reala e „inca nedecisa".
                    ⚠️ Celalalt loc cu acelasi text vechi (`useSchoolConfirmationWithForm.ts`) NU
                    se atinge: acolo profesorul chiar a declarat ca nu participa. */}
                ⚠️ În proces de aprobare
              </AlertDescription>
            </Alert>
          )}

          <SidebarGroupContent className="flex-1 min-h-0">
            <ScrollArea className="h-full">
              {(() => {
                // Filter links based on role, conditions, and temporary school update logic
                const filteredLinks = PROFILE_LINKS.filter((link) => {
                  if (shouldHideMenuItems && link.PATH !== '/actualizare-detalii-scoala') return false;
                  // includes(undefined) dadea false oricum; acum se si vede in tipuri
                  if (!userInfo?.role || !link.ALLOWED_ROLES.includes(userInfo.role)) return false;
                  if (link.SHOW_CONDITION && !link.SHOW_CONDITION(userInfo)) return false;
                  // Poarta de stagiu trece prin ACELASI decident ca navigarea
                  // (classifyPathForRole cu `stage`), nu printr-un `if` nou aici. Cat timp
                  // confirmarea inscrierii e ceruta, singura ruta permisa e ecranul de
                  // confirmare, care are showInSidebar: false: lista ramane goala.
                  // Containerul NU se ascunde, pentru ca NavUser din SidebarFooter e singurul
                  // logout al aplicatiei.
                  if (
                    !isPathAllowedForRole(link.PATH, userInfo.role, NAV_LINKS_V2, {
                      needsSchoolRegistrationConfirmation: userInfo.needsSchoolRegistrationConfirmation,
                      confirmationPath: PATHS[PAGES.SCHOOL_REGISTRATION_CONFIRMATION],
                    })
                  ) return false;
                  return true;
                });

                // Group the filtered links
                const groupedLinks = groupLinksByMenuGroup(filteredLinks);

                // Doar grupurile nevide; separatorul intre oricare doua consecutive (`grupuriDeAfisat`).
                const grupuri = grupuriDeAfisat(groupedLinks);

                return grupuri.map((groupKey, groupIndex) => {
                  const groupLinks = groupedLinks[groupKey];

                  return (
                    <div key={groupKey}>
                      <SidebarGroup>
                        <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-sidebar-foreground px-2 py-2">
                          {GROUP_LABELS[groupKey]}
                        </SidebarGroupLabel>
                        <SidebarGroupContent>
                          <SidebarMenu>
                            {groupLinks.map((link: any, idx: number) => {
                              const displayName =
                                link.TEXT === "Raport Conturi Elevi" && userInfo?.role === ROLES.PROFESOR
                                  ? "Elevii mei"
                                  : link.TEXT;

                              // Determine font weight based on role and item type
                              const isProfessorPreviewOnly =
                                userInfo?.role === ROLES.PROFESOR &&
                                PROFESOR_PREVIEW_ONLY_PATHS.includes(link.PATH);

                              return (
                                <SidebarMenuItem key={`${groupKey}-${idx}`}>
                                  <SidebarMenuButton
                                    asChild
                                    isActive={location.pathname === link.PATH}
                                    className={isProfessorPreviewOnly ? 'ecotic-sidebar-menu-medium' : 'ecotic-sidebar-menu-bold'}
                                  >
                                    <button onClick={() => { navigate(link.PATH); setOpenMobile(false); }}>
                                      <span className="break-words">{displayName}</span>
                                    </button>
                                  </SidebarMenuButton>
                                </SidebarMenuItem>
                              );
                            })}
                          </SidebarMenu>
                        </SidebarGroupContent>
                      </SidebarGroup>
                      {/* Separator intre grupurile afisate (nu dupa ultimul) */}
                      {groupIndex < grupuri.length - 1 && <SidebarSeparator className="my-2" />}
                    </div>
                  );
                });
              })()}
            </ScrollArea>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="ecotic-sidebar-section">
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
